export type TJsonValue = string | number | boolean | null | TJsonValue[] | {[key: string]: TJsonValue}

export type TApplicationError = {
    type: 'application'
    kind: 'exception' | 'rejection' | 'render'
    message: string
    stack?: string
    source?: string
    line?: number
    column?: number
    componentStack?: string
}

export type TSolverError = {
    type: 'solver'
    source: 'local' | 'remote'
    message: string
    context?: {[key: string]: TJsonValue}
}

export type TApiError = {
    type: 'api'
    method: string
    url: string
    status: number | null
    text: string
}

export type TError = TApplicationError | TSolverError | TApiError
export type TErrorReport = TError & {ts: string}

export type TErrorShipment = {
    errors: TErrorReport[]
    suppressed: number
}

const BURST_LIMIT = 10
const RATE_INTERVAL_MS = 30_000

/**
 * Buffers semantic PAW errors for the existing telemetry batch. Repeated
 * reports remain separate records; a global burst limiter bounds a flood.
 */
export class ErrorMonitor {
    private errors: TErrorReport[] = []
    private suppressed = 0
    private burstAcceptedAt: number[] = []
    private limited = false
    private lastSeenAt?: number
    private lastAcceptedAt?: number

    constructor (private readonly now: () => number = Date.now) {}

    report (error: TError): void {
        try {
            const now = this.now()

            // A quiet interval starts a fresh burst. Once limited, continuous
            // traffic stays limited and receives one sample per interval.
            if (this.lastSeenAt !== undefined && now - this.lastSeenAt >= RATE_INTERVAL_MS) {
                this.resetRateWindow()
            }
            this.lastSeenAt = now

            if (!this.limited) {
                this.burstAcceptedAt = this.burstAcceptedAt.filter(
                    acceptedAt => now - acceptedAt < RATE_INTERVAL_MS,
                )
            }

            if (!this.limited && this.burstAcceptedAt.length < BURST_LIMIT) {
                this.accept(error, now)
                this.burstAcceptedAt.push(now)
                return
            }

            this.limited = true
            if (this.lastAcceptedAt !== undefined && now - this.lastAcceptedAt >= RATE_INTERVAL_MS) {
                this.accept(error, now)
                return
            }

            this.suppressed += 1
        } catch {
            // Error reporting must never destabilize the application path.
        }
    }

    peek (): TErrorShipment {
        return {errors: [...this.errors], suppressed: this.suppressed}
    }

    /** Clear only the shipped batch; rate state survives across shipments. */
    clear (): void {
        this.errors = []
        this.suppressed = 0
    }

    private accept (error: TError, now: number): void {
        this.errors.push({...error, ts: new Date(now).toISOString()} as TErrorReport)
        this.lastAcceptedAt = now
    }

    private resetRateWindow (): void {
        this.burstAcceptedAt = []
        this.limited = false
        this.lastAcceptedAt = undefined
    }
}

export const errorMonitor = new ErrorMonitor()

/** Convert thrown browser values into the application-error contract. */
export function captureApplicationError (
    {monitor, kind, error, details}: {
        monitor: ErrorMonitor
        kind: TApplicationError['kind']
        error: unknown
        details?: Omit<TApplicationError, 'type' | 'kind' | 'message' | 'stack'>
    },
): void {
    try {
        monitor.report({
            type: 'application',
            kind,
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            ...details,
        })
    } catch {
        // Browser error hooks must never throw while handling another error.
    }
}

/** Record a solver failure as a high-level semantic report, not a raw throw. */
export function captureSolverError (
    {monitor, source, message, context}: {
        monitor: ErrorMonitor
        source: TSolverError['source']
        message: string
        context?: TSolverError['context']
    },
): void {
    try {
        monitor.report({type: 'solver', source, message, context})
    } catch {
        // Error reporting must never destabilize route handling.
    }
}

/** Intentional connectivity probes are not actionable API failures. */
export function isHealthCheck (method: string, url: string): boolean {
    try {
        const parsed = new URL(url)
        const normalizedMethod = String(method).toUpperCase()
        if (normalizedMethod === 'PUT' && parsed.pathname.endsWith('/route') && !parsed.search) {
            return true
        }
        return normalizedMethod === 'GET' && parsed.pathname.endsWith('/ping')
    } catch {
        return false
    }
}

function isRemoteSolverRequest (method: string, url: string): boolean {
    try {
        const parsed = new URL(url)
        return String(method).toUpperCase() === 'PUT'
            && parsed.pathname.endsWith('/route')
            && parsed.searchParams.has('paths')
    } catch {
        return false
    }
}

function errorMessage (error: unknown): string {
    if (error instanceof Error) return error.message
    if (error && typeof error === 'object' && 'error' in error) {
        return String(error.error)
    }
    return String(error)
}

function captureRemoteSolverError (
    monitor: ErrorMonitor,
    method: string,
    url: string,
    status: number,
    text: string,
): void {
    if (!isRemoteSolverRequest(method, url)) return
    captureSolverError({
        monitor,
        source: 'remote',
        message: text,
        context: {method: String(method).toUpperCase(), url: String(url), status},
    })
}

/** Capture a request that failed before an HTTP response existed. */
export function captureApiException (
    {monitor, method, url, error}: {
        monitor: ErrorMonitor
        method: string
        url: string
        error: unknown
    },
): void {
    if (isHealthCheck(method, url)) return
    const normalizedMethod = String(method).toUpperCase()
    const normalizedUrl = String(url)
    const text = errorMessage(error)
    monitor.report({
        type: 'api', method: normalizedMethod, url: normalizedUrl, status: null, text,
    })
}

/** Capture an HTTP response error without consuming the caller's response. */
export async function captureApiResponse (
    {monitor, method, url, response, acceptedStatuses = []}: {
        monitor: ErrorMonitor
        method: string
        url: string
        response: Response
        acceptedStatuses?: number[]
    },
): Promise<void> {
    if (response.ok || acceptedStatuses.includes(response.status) || isHealthCheck(method, url)) return

    let text = response.statusText
    try {
        text = await response.clone().text() || response.statusText
    } catch {
        // statusText is the best available server response description.
    }

    const normalizedMethod = String(method).toUpperCase()
    const normalizedUrl = String(url)
    monitor.report({
        type: 'api',
        method: normalizedMethod,
        url: normalizedUrl,
        status: response.status,
        text,
    })
    captureRemoteSolverError(monitor, normalizedMethod, normalizedUrl, response.status, text)
}
